/**
 * /me/archive — soft-deleted workspaces, projects, and tasks the current
 * user can see. Each row has a "Restore" action that PATCHes
 * `archived: false` (or, for tasks, the equivalent PATCH), and a
 * "Delete permanently" for tasks when the user owns them.
 *
 * Backing endpoint: `GET /api/v1/me/archive`.
 */

import { useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import {
  useArchive,
  useRestoreWorkspace,
} from '@/features/workspaces/workspaces'
import { useRestoreProject, useRestoreTask, useDeleteTaskHard } from '@/features/projects/board'
import { toast } from '@/features/feedback/toast'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

type Filter = 'all' | 'workspaces' | 'projects' | 'tasks'

export function ArchivePage() {
  const { data, isLoading, error, refetch, isFetching } = useArchive()
  const [filter, setFilter] = useState<Filter>('all')

  const totals = useMemo(() => {
    return {
      workspaces: data?.workspaces.length ?? 0,
      projects: data?.projects.length ?? 0,
      tasks: data?.tasks.length ?? 0,
    }
  }, [data])

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-2 border-b border-surface-200 dark:border-surface-700 bg-white/60 dark:bg-surface-900/60 backdrop-blur px-4 md:pr-14 py-3">
        <h1 className="text-sm font-semibold">Archive</h1>
        <span className="text-xs text-surface-500">
          Soft-deleted items you can restore.
        </span>
        <div className="ml-auto flex items-center gap-1">
          <FilterButton active={filter === 'all'} onClick={() => setFilter('all')}>
            All
            <span className="ml-1 text-[10px] text-surface-500">
              {totals.workspaces + totals.projects + totals.tasks}
            </span>
          </FilterButton>
          <FilterButton active={filter === 'workspaces'} onClick={() => setFilter('workspaces')}>
            Workspaces
            <span className="ml-1 text-[10px] text-surface-500">{totals.workspaces}</span>
          </FilterButton>
          <FilterButton active={filter === 'projects'} onClick={() => setFilter('projects')}>
            Projects
            <span className="ml-1 text-[10px] text-surface-500">{totals.projects}</span>
          </FilterButton>
          <FilterButton active={filter === 'tasks'} onClick={() => setFilter('tasks')}>
            Tasks
            <span className="ml-1 text-[10px] text-surface-500">{totals.tasks}</span>
          </FilterButton>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="ml-2 rounded-md border border-surface-300 dark:border-surface-700 px-2 py-1 text-xs hover:border-brand-500 disabled:opacity-50"
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-auto p-4">
        {isLoading && (
          <div className="flex h-full items-center justify-center text-sm text-surface-500">
            Loading archive…
          </div>
        )}
        {error && (
          <div className="flex h-full items-center justify-center text-sm text-red-500">
            Failed to load archive.
          </div>
        )}
        {data && totals.workspaces + totals.projects + totals.tasks === 0 && (
          <div className="flex h-full items-center justify-center">
            <div className="card max-w-md p-6 text-center">
              <h2 className="mb-1 text-base font-semibold">Nothing archived</h2>
              <p className="text-sm text-surface-500">
                When you archive a workspace, project, or task it'll show up here so
                you can restore it later.
              </p>
            </div>
          </div>
        )}

        {data && (filter === 'all' || filter === 'workspaces') && data.workspaces.length > 0 && (
          <Section
            title={`Workspaces (${data.workspaces.length})`}
            hint="Restoring brings back the workspace and its projects and tasks."
          >
            {data.workspaces.map((w) => (
              <WorkspaceRow key={w.id} item={w} />
            ))}
          </Section>
        )}

        {data && (filter === 'all' || filter === 'projects') && data.projects.length > 0 && (
          <Section
            title={`Projects (${data.projects.length})`}
            hint="Restoring brings the project back into the sidebar and its tasks back to the board."
          >
            {data.projects.map((p) => (
              <ProjectRow key={p.id} item={p} />
            ))}
          </Section>
        )}

        {data && (filter === 'all' || filter === 'tasks') && data.tasks.length > 0 && (
          <Section
            title={`Tasks (${data.tasks.length})`}
            hint="Restoring returns the task to its column. Deleting permanently removes the row."
          >
            {data.tasks.map((t) => (
              <TaskRow key={t.id} item={t} />
            ))}
          </Section>
        )}
      </div>
    </div>
  )
}

function Section({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <section className="mb-6">
      <div className="mb-2 flex items-baseline gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-surface-500">
          {title}
        </h2>
        {hint && <span className="text-xs text-surface-500">{hint}</span>}
      </div>
      <div className="card divide-y divide-surface-200 dark:divide-surface-700">
        {children}
      </div>
    </section>
  )
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-md border px-2 py-1 text-xs',
        active
          ? 'border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-300'
          : 'border-surface-300 dark:border-surface-700 text-surface-600 dark:text-surface-300 hover:border-brand-500',
      )}
    >
      {children}
    </button>
  )
}

// ────────────── Row components ──────────────

function WorkspaceRow({ item }: { item: ReturnType<typeof useArchive>['data'] extends infer T ? T extends { workspaces: ReadonlyArray<infer U> } ? U : never : never }) {
  const navigate = useNavigate()
  const restore = useRestoreWorkspace()
  const canRestore = item.role === 'owner' || item.role === 'admin'
  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <span className="flex h-7 w-7 items-center justify-center rounded bg-surface-300 dark:bg-surface-700 text-[10px] font-mono">
        {(item.slug || item.name).slice(0, 2).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{item.name}</div>
        <div className="truncate text-[11px] text-surface-500">
          /{item.slug} · archived {formatRelative(item.archivedAt)} · role: {item.role}
        </div>
      </div>
      <button
        disabled={!canRestore || restore.isPending}
        onClick={async () => {
          try {
            await restore.mutateAsync(item.id)
            toast(`Workspace "${item.name}" restored`, { kind: 'success' })
            navigate({ to: '/w/$wsId', params: { wsId: item.id } })
          } catch (e) {
            toast(e instanceof ApiError ? e.message : 'Failed to restore workspace', { kind: 'error' })
          }
        }}
        className="rounded-md border border-surface-300 dark:border-surface-700 px-2 py-1 text-xs text-surface-700 dark:text-surface-200 hover:border-brand-500 disabled:opacity-50"
        title={canRestore ? 'Restore this workspace' : 'Only owners and admins can restore a workspace'}
      >
        {restore.isPending ? 'Restoring…' : 'Restore'}
      </button>
    </div>
  )
}

function ProjectRow({ item }: { item: ReturnType<typeof useArchive>['data'] extends infer T ? T extends { projects: ReadonlyArray<infer U> } ? U : never : never }) {
  const restore = useRestoreProject()
  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <span
        className="flex h-7 w-7 items-center justify-center rounded font-mono text-[10px] text-white"
        style={{ backgroundColor: item.color }}
      >
        {item.key.slice(0, 2)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{item.name}</div>
        <div className="truncate text-[11px] text-surface-500">
          {item.key} · archived {formatRelative(item.archivedAt)}
        </div>
      </div>
      <button
        disabled={restore.isPending}
        onClick={async () => {
          try {
            await restore.mutateAsync(item.id)
            toast(`Project "${item.name}" restored`, { kind: 'success' })
          } catch (e) {
            toast(e instanceof ApiError ? e.message : 'Failed to restore project', { kind: 'error' })
          }
        }}
        className="rounded-md border border-surface-300 dark:border-surface-700 px-2 py-1 text-xs text-surface-700 dark:text-surface-200 hover:border-brand-500 disabled:opacity-50"
      >
        {restore.isPending ? 'Restoring…' : 'Restore'}
      </button>
    </div>
  )
}

function TaskRow({ item }: { item: ReturnType<typeof useArchive>['data'] extends infer T ? T extends { tasks: ReadonlyArray<infer U> } ? U : never : never }) {
  const restore = useRestoreTask(item.workspaceId, item.projectId)
  const del = useDeleteTaskHard(item.workspaceId, item.projectId)
  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <span className="flex h-7 w-7 items-center justify-center rounded bg-surface-300 dark:bg-surface-700 text-[10px] font-mono">
        {item.projectKey.slice(0, 2)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">
          <span className="font-mono text-[11px] text-surface-500">{item.projectKey}-{item.key}</span>{' '}
          {item.title}
        </div>
        <div className="truncate text-[11px] text-surface-500">
          {item.projectName} · status: {item.status} · archived {formatRelative(item.archivedAt)}
        </div>
      </div>
      <button
        disabled={restore.isPending}
        onClick={async () => {
          try {
            await restore.mutateAsync(item.id)
            toast(`Task restored`, { kind: 'success' })
          } catch (e) {
            toast(e instanceof ApiError ? e.message : 'Failed to restore task', { kind: 'error' })
          }
        }}
        className="rounded-md border border-surface-300 dark:border-surface-700 px-2 py-1 text-xs text-surface-700 dark:text-surface-200 hover:border-brand-500 disabled:opacity-50"
      >
        {restore.isPending ? 'Restoring…' : 'Restore'}
      </button>
      <button
        disabled={del.isPending}
        onClick={async () => {
          if (!confirm(`Delete task "${item.title}" permanently? This cannot be undone.`)) return
          try {
            await del.mutateAsync(item.id)
            toast(`Task deleted`, { kind: 'success' })
          } catch (e) {
            toast(e instanceof ApiError ? e.message : 'Failed to delete task', { kind: 'error' })
          }
        }}
        className="rounded-md border border-red-300 dark:border-red-700 px-2 py-1 text-xs text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
      >
        {del.isPending ? 'Deleting…' : 'Delete permanently'}
      </button>
    </div>
  )
}

// ────────────── Helpers ──────────────

function formatRelative(iso: string | null): string {
  if (!iso) return 'unknown'
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 0) return 'just now'
  const min = Math.floor(ms / 60_000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const d = Math.floor(hr / 24)
  if (d < 30) return `${d}d ago`
  return new Date(iso).toLocaleDateString()
}
